import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeftIcon, 
  ShieldCheckIcon, 
  DocumentCheckIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import SearchableSelect from '../components/SearchableSelect';
import api from '../services/api';
import toast from '../utils/toast';

const Verification = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [formData, setFormData] = useState({
    idType: '',
    idNumber: '',
    businessName: '',
    businessType: '',
    businessAddress: '',
    purpose: ''
  });

  // Ghana ID types
  const idTypes = [
    { value: 'national_id', label: 'Ghana National ID', icon: '🆔', description: 'National identification card' },
    { value: 'drivers_license', label: 'Driver\'s License', icon: '🚗', description: 'Driving permit from DVLA' },
    { value: 'ssnit', label: 'SSNIT Card', icon: '💼', description: 'Social Security number' },
    { value: 'ghana_card', label: 'Ghana Card', icon: '🇬🇭', description: 'New biometric ID card' }
  ];

  const businessTypes = [
    { value: 'startup', label: 'Startup', icon: '🚀', description: 'Early-stage company' },
    { value: 'sme', label: 'Small & Medium Enterprise', icon: '🏢', description: 'Established SME' },
    { value: 'corporation', label: 'Corporation', icon: '🏛️', description: 'Large corporation' },
    { value: 'ngo', label: 'NGO/Non-Profit', icon: '❤️', description: 'Charitable organization' },
    { value: 'individual', label: 'Individual/Freelancer', icon: '👤', description: 'Solo practitioner' },
    { value: 'other', label: 'Other', icon: '⚡', description: 'Other business type' }
  ];

  const purposes = [
    { value: 'customer_support', label: 'Customer Support', icon: '💬', description: 'Help customers' },
    { value: 'marketing', label: 'Marketing & Promotions', icon: '📊', description: 'Promotional messaging' },
    { value: 'sales', label: 'Sales & Lead Generation', icon: '💰', description: 'Sales conversations' },
    { value: 'notifications', label: 'Automated Notifications', icon: '🔔', description: 'System notifications' },
    { value: 'education', label: 'Education & Training', icon: '🎓', description: 'Educational content' },
    { value: 'community', label: 'Community Management', icon: '👥', description: 'Community engagement' },
    { value: 'other', label: 'Other Business Purpose', icon: '⚙️', description: 'Other business use' }
  ];

  // Fetch existing verification documents
  const fetchExistingDocuments = async () => {
    try {
      const response = await api.getVerificationDocuments();
      if (response.data && response.data.documents) {
        setExistingDocuments(response.data.documents);
      }
    } catch (error) {
      console.error('Error fetching verification documents:', error);
    }
  };

  // Fetch user profile data and populate form
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await api.getUserProfile();
        setUserProfile(profile.data);
        
        // Auto-populate form data from organization profile
        if (profile.data && profile.data.organization) {
          const org = profile.data.organization;
          
          // Parse existing verification data if available
          let existingVerificationData = {};
          if (org.verificationNotes) {
            try {
              existingVerificationData = JSON.parse(org.verificationNotes);
            } catch (e) {
              console.log('Could not parse verification notes:', e);
            }
          }
          
          setFormData(prev => ({
            ...prev,
            // Use existing verification data first, then organization data, then current values
            idType: existingVerificationData.id_type || prev.idType,
            idNumber: existingVerificationData.id_number || prev.idNumber,
            businessName: existingVerificationData.business_name || org.name || prev.businessName,
            businessType: existingVerificationData.business_type || org.businessType || prev.businessType,
            businessAddress: existingVerificationData.business_address || org.address || prev.businessAddress,
            purpose: existingVerificationData.purpose || prev.purpose,
          }));
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Use fallback data from AuthContext if available
        if (user) {
          setUserProfile({
            user: {
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              email: user.email || '',
              phone: ''
            }
          });
        }
      }
    };
    
    const initializeData = async () => {
      await fetchUserProfile();
      await fetchExistingDocuments();
    };
    
    initializeData();
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      id: Math.random().toString(36).substr(2, 9)
    }));

    setUploadedFiles(prev => {
      const combined = [...prev, ...newFiles];
      if (combined.length > 5) {
        toast.error('Maximum 5 files allowed');
        return prev;
      }
      return combined;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc', '.docx']
    },
    multiple: true,
    maxFiles: 5
  });

  const removeFile = (id) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadedFiles.length === 0 && existingDocuments.length === 0) {
      toast.error('Please upload at least one ID document');
      return;
    }

    setLoading(true);
    
    try {
      const submissionData = new FormData();
      
      // Add form data
      Object.keys(formData).forEach(key => {
        submissionData.append(key, formData[key]);
      });
      
      // Add personal information from user profile
      if (userProfile && userProfile.user) {
        submissionData.append('firstName', userProfile.user.firstName || '');
        submissionData.append('lastName', userProfile.user.lastName || '');
        submissionData.append('phoneNumber', userProfile.user.phone || '');
      }
      
      // Add files
      uploadedFiles.forEach((fileObj, index) => {
        submissionData.append(`documents`, fileObj.file);
      });
      
      await api.submitVerificationDocuments(submissionData);
      
      toast.success('Verification documents submitted successfully!');
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Verification submission error:', error);
      toast.error('Failed to submit verification documents');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Account Verification</h1>
              <p className="text-gray-600">Complete your business verification to unlock all features</p>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Personal information is managed in your{' '}
                  <button
                    onClick={() => navigate('/dashboard/profile')}
                    className="underline font-medium hover:text-blue-800"
                  >
                    profile settings
                  </button>
                  . This form focuses on business verification only.
                </p>
                {!userProfile?.user?.phone && (
                  <p className="text-sm text-red-700 mt-2">
                    <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                    Please update your details in your <button onClick={() => navigate('/dashboard/profile')} className="underline font-medium hover:text-red-800">profile settings</button> to proceed with verification.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* ID Verification */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              <ShieldCheckIcon className="h-6 w-6 inline mr-2 text-whatsapp-600" />
              Identity Verification
            </h2>
            <p className="text-gray-600 mb-6">Please provide a valid government-issued ID for identity verification.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SearchableSelect
                label="ID Type"
                name="idType"
                options={idTypes}
                value={formData.idType}
                onChange={handleInputChange}
                placeholder="Search or select ID type..."
                isRequired={true}
                isSearchable={true}
                isClearable={false}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="idNumber"
                  required
                  value={formData.idNumber}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                  placeholder="Enter your ID number"
                />
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              <DocumentCheckIcon className="h-6 w-6 inline mr-2 text-whatsapp-600" />
              Business Information
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="businessName"
                  required
                  value={formData.businessName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                  placeholder="Your company or business name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SearchableSelect
                  label="Business Type"
                  name="businessType"
                  options={businessTypes}
                  value={formData.businessType}
                  onChange={handleInputChange}
                  placeholder="Search or select business type..."
                  isRequired={true}
                  isSearchable={true}
                  isClearable={true}
                />

                <SearchableSelect
                  label="Business Purpose"
                  name="purpose"
                  options={purposes}
                  value={formData.purpose}
                  onChange={handleInputChange}
                  placeholder="Search or select purpose..."
                  isRequired={true}
                  isSearchable={true}
                  isClearable={true}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="businessAddress"
                  required
                  value={formData.businessAddress}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                  placeholder="Enter your business address"
                />
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              <CloudArrowUpIcon className="h-6 w-6 inline mr-2 text-whatsapp-600" />
              Upload Documents <span className="text-red-500">*</span>
            </h2>
            <p className="text-gray-600 mb-6">Upload clear photos of your ID documents (max 5 files) - Required for verification</p>

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragActive 
                  ? 'border-whatsapp-500 bg-whatsapp-50' 
                  : 'border-gray-300 hover:border-whatsapp-400 hover:bg-gray-50'
                }`}
            >
              <input {...getInputProps()} />
              <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              {isDragActive ? (
                <p className="text-whatsapp-600 font-medium">Drop the files here...</p>
              ) : (
                <div>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium text-whatsapp-600">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-sm text-gray-500">PNG, JPG, GIF, PDF, DOCX up to 2MB each (max 5 files)</p>
                </div>
              )}
            </div>

            {/* File Preview */}
            {(existingDocuments.length > 0 || uploadedFiles.length > 0) && (
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Documents ({existingDocuments.length + uploadedFiles.length}/5)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {/* Existing Documents */}
                  {existingDocuments.map((doc) => (
                    <div key={doc.id} className="relative">
                      <div className="aspect-square border-2 border-green-200 rounded-lg overflow-hidden bg-green-50">
                        <div className="w-full h-full flex flex-col items-center justify-center p-2">
                          <DocumentCheckIcon className="h-8 w-8 text-green-600 mb-2" />
                          <span className="text-xs text-green-700 font-medium">
                            {doc.verification_status === 'pending' ? 'Under Review' : 
                             doc.verification_status === 'approved' ? 'Approved' : 'Rejected'}
                          </span>
                        </div>
                      </div>
                      <div className="absolute -top-2 -left-2 bg-green-500 text-white rounded-full p-1">
                        <CheckCircleIcon className="h-4 w-4" />
                      </div>
                      <p className="mt-2 text-xs text-gray-600 truncate" title={doc.document_name}>
                        {doc.document_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                  
                  {/* Newly Uploaded Files */}
                  {uploadedFiles.map((fileObj) => (
                    <div key={fileObj.id} className="relative">
                      <div className="aspect-square border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                        {fileObj.preview ? (
                          <img
                            src={fileObj.preview}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-2">
                            <DocumentCheckIcon className="h-8 w-8 text-gray-400 mb-2" />
                            <span className="text-xs text-gray-600 font-medium text-center">
                              {fileObj.file.type.includes('pdf') ? 'PDF' : 'DOC'}
                            </span>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(fileObj.id)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <p className="mt-2 text-xs text-gray-600 truncate">{fileObj.file.name}</p>
                      <p className="text-xs text-blue-600 font-medium">New Upload</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <div className="text-sm text-gray-600">
                <p><strong>Next:</strong> Our team will review your documents within 1-2 business days.</p>
                <p>You'll receive an email notification once verification is complete.</p>
              </div>
              
              <button
                type="submit"
                disabled={loading || (uploadedFiles.length === 0 && existingDocuments.length === 0) || !userProfile?.user?.phone}
                className="px-8 py-3 bg-gradient-to-r from-whatsapp-500 to-green-600 text-white font-medium rounded-lg hover:from-whatsapp-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                ) : (
                  'Submit for Verification'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Verification;